/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/naming-convention */
import { Card, Empty, Form, Switch } from 'antd';
import { Fragment, VFC, useCallback, useEffect } from 'react';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { mergeDeepRight, omit } from 'rambda';
import { FormItem } from 'components/Form/FormItem';
import { Select } from 'components/Select/Select';
import { getMeterMountTechnologySelectOptions } from 'routes/inventorize_meter/steps/reading/utils/getMeterMountTechnologyOptions';
import {
  OutputTechnology,
  SerializedInventoryMeterOutput,
  UpdateInventoryMeterOutputDto,
} from '@enmon/commons/lib/tools';
import { FormattedMessage, useIntl } from 'react-intl';
import { InstallMeterStep } from 'routes/installation/enums/installMeterStep';
import { Input } from 'components/Input/Input';
import { AddonOutputSelect } from 'components/Select/MeterAddonOutput';
import { Divider } from 'components/Divider/Divider';
import { InstallationDetailStepFormValues, InstallationMeterRegisterFormValues } from './slice';
import { useStore } from '../../useStore';
import { useInstallMeterStepsStore } from '../../components/store';
import { INSTALL_METER_DETAIL_FORM_LAYOUT_CONFIG } from './const';
import { RegisterSection } from './RegisterSection';
import { InstallMeterDetailStepContent as messages } from './messages';

export const InstallMeterDetailStepContent: VFC = () => {
  const intl = useIntl();
  const [form] = Form.useForm<InstallationDetailStepFormValues>();
  const { setNextStep } = useInstallMeterStepsStore();

  const { enmonMeter, outputs, registers, meterMountTechnology, setInstallationDetailStepValues, setIsStepValid } =
    useStore();

  const validateForm = useCallback(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => {
        setIsStepValid(InstallMeterStep.InstallationDetail, true);
      })
      .catch((result: ValidateErrorEntity) => {
        setIsStepValid(InstallMeterStep.InstallationDetail, result.errorFields.length === 0);
      });
  }, [form, setIsStepValid]);

  // validate form on mount in case that there are not registers
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  if (!enmonMeter) {
    return null;
  }

  const handleValuesChanged = (
    changedValues: Partial<InstallationDetailStepFormValues>,
    values: InstallationDetailStepFormValues,
  ) => {
    if ('outputs' in changedValues) {
      changedValues.outputs?.forEach(output => {
        if (output.is_occupied === false) output.remote_reading_owner = undefined;
      });
    }
    validateForm();
    setInstallationDetailStepValues(prevState => ({
      registersCountersReadingAt: new Date(),
      registers: mergeDeepRight<InstallationMeterRegisterFormValues[]>(prevState.registers, values.registers),
      outputs: changedValues.outputs
        ? mergeDeepRight<UpdateInventoryMeterOutputDto[]>(prevState.outputs, changedValues.outputs)
        : prevState.outputs,
      meterMountTechnology: values.meterMountTechnology,
    }));
  };

  const handleFormFinish = (formValues: InstallationDetailStepFormValues) => {
    setInstallationDetailStepValues(prevState => ({
      registers: mergeDeepRight<InstallationMeterRegisterFormValues[]>(prevState.registers, formValues.registers).map(
        register =>
          formValues.meterMountTechnology !== OutputTechnology.Impulse
            ? omit(['factor', 'channel'], register)
            : register,
      ),
    }));
    setNextStep();
  };

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false}>
      <Form<InstallationDetailStepFormValues>
        form={form}
        id="installMeterDetailStepForm"
        name="basic"
        {...INSTALL_METER_DETAIL_FORM_LAYOUT_CONFIG}
        layout="horizontal"
        onFinish={handleFormFinish}
        onValuesChange={handleValuesChanged}
      >
        <FormItem
          initialValue={meterMountTechnology}
          label={<FormattedMessage {...messages.meterMountTechnologyLabel} />}
          name="meterMountTechnology"
          tooltip={<FormattedMessage {...messages.meterMountTechnologyTooltip} />}
        >
          <Select
            allowClear
            className="w-100"
            options={getMeterMountTechnologySelectOptions(enmonMeter.commodity, intl)}
            placeholder={<FormattedMessage {...messages.meterMountTechnologyPlaceholder} />}
          />
        </FormItem>
        {outputs.length > 0 && (
          <>
            <Divider>
              <FormattedMessage {...messages.inbuiltOutputs} />
            </Divider>

            <Form.List initialValue={outputs} name="outputs">
              {fields =>
                fields.map((field, index) => (
                  <Fragment key={field.key}>
                    <FormItem
                      label={`${intl.formatMessage(messages.technology)} ${index + 1}`}
                      name={[field.name, 'technology' satisfies keyof SerializedInventoryMeterOutput]}
                    >
                      <AddonOutputSelect disabled />
                    </FormItem>
                    {outputs?.[field.name]?.is_occupied === true && (
                      <FormItem
                        label={<FormattedMessage {...messages.remoteReadingOwner} />}
                        name={[field.name, 'remote_reading_owner' satisfies keyof SerializedInventoryMeterOutput]}
                      >
                        <Input placeholder={`${intl.formatMessage(messages.remoteReadingOwner)} ${index + 1}`} />
                      </FormItem>
                    )}
                    <FormItem
                      label={<FormattedMessage {...messages.isOccupied} />}
                      name={[field.name, 'is_occupied' satisfies keyof SerializedInventoryMeterOutput]}
                      valuePropName="checked"
                    >
                      <Switch />
                    </FormItem>
                  </Fragment>
                ))
              }
            </Form.List>
          </>
        )}
        {enmonMeter.registers.length === 0 ? (
          <Empty description={<FormattedMessage {...messages.noRegisters} />} />
        ) : (
          <>
            <Divider>
              <FormattedMessage {...messages.registers} />
            </Divider>
            {registers.map((register, index) => (
              <RegisterSection key={register._id ?? `register-${index}`} index={index} register={register} />
            ))}
          </>
        )}
      </Form>
    </Card>
  );
};
